import { unminifyError } from '@sgme/stacktrace';
import { documentApi, DocumentError } from 'apiConnector/document/document';
import type { IntlKey } from 'locales';
import type { BlobFileResponse } from 'models/FileResponse';
import type { IBlotterItem } from 'models/IBlotterItem';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { downloadFile } from 'utils/documents/downloadDocument';
import { getTermsheetFilename } from 'utils/documents/filenames';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

export const useDownloadTermsheet = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<DocumentError | null>(null);
  const { formatMessage } = useIntl();

  const downloadTermsheet = useCallback(
    (productId: string, blotterItem: IBlotterItem, language?: string) => {
      setIsDownloading(true);

      const termsheet$: Observable<BlobFileResponse> = documentApi.downloadTermsheet(productId, language).pipe(
        finalize(() => {
          setIsDownloading(false);
        }),
      );
      termsheet$.subscribe({
        next({ data, filename }: BlobFileResponse) {
          const name = filename === undefined || filename === '' ? getTermsheetFilename(blotterItem) : filename;
          const lang = language ? ` (${formatMessage({ id: `languageCode.${language}` as IntlKey })})` : '';
          downloadFile(data, `${name}${lang}.pdf`);
        },
        async error(err: DocumentError) {
          const e = await unminifyError(err);
          getFrontLogger().error('Failure to download termsheet for {productId} {@FrontError}', productId, e);
          setError(err);
        },
      });
    },
    [formatMessage],
  );

  return {
    isDownloading,
    downloadTermsheet,
    error,
  };
};
